<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { EditorState } from '@common/Common'
    import
        {
            CreateExpenseAccountEventPayload,
            UpdateCostAccountEventPayload
        } from '@Quest/Costs/Components/ExpenseAccountEditor'
    import type { ExpenseAccountEditorViewModel } from '@Quest/Costs/Components/ExpenseAccountEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ExpenseAccountEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-expense', payload: CreateExpenseAccountEventPayload): void
        (e: 'update-cost', payload: UpdateCostAccountEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create' : 'Update'
    )

    const modalTitle = computed(() =>
        vm.value.state === EditorState.Creator
            ? 'New expense account'
            : 'Edit the cost account'
    )

    const canEditName = computed(() => vm.value.canEditName)
    const canEditClassification = computed(() => vm.value.canEditClassification)
    const subtotals = computed(() => vm.value.subtotals)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string | null>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const classification = computed<string | null>({
        get: () => vm.value.classification,
        set: v => { vm.value.classification = v }
    })

    const subtotalId = computed<string | null>({
        get: () => vm.value.subtotalId,
        set: v => { vm.value.subtotalId = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        if (vm.value.state === EditorState.Creator)
        {
            emit(
                'create-expense',
                new CreateExpenseAccountEventPayload(
                    name.value,
                    classification.value,
                    subtotalId.value
                )
            )
        }
        else if (vm.value.state === EditorState.Editor)
        {
            emit(
                'update-cost',
                new UpdateCostAccountEventPayload(
                    vm.value.id,
                    name.value,
                    classification.value,
                    subtotalId.value
                )
            )
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="expense-account-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-if="canEditName" v-model.trim="name" class="form-control" required maxlength="100" minlength="2" />
                                <input type="text" v-if="!canEditName" v-model="name" class="form-control" readonly />
                            </div>
                            <div class="form-group">
                                <label for="subtotal">Subtotal:</label>
                                <select id="subtotal" v-model="subtotalId" class="form-control">
                                    <option :value="null">None</option>
                                    <option v-for="s in subtotals" :value="s.Id">{{s.Name}}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label for="classification">Classification:</label>
                                <select v-if="canEditClassification" id="classification" v-model="classification" class="form-control">
                                    <option value="OpEx">OpEx</option>
                                    <option value="COGS">COGS</option>
                                    <option value="Other">Other expenses</option>
                                </select>
                                <input v-else type="text" readonly v-model="classification" class="form-control" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="expense-account-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>